import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Calendar, ArrowRight, Sparkles, BookOpen, Send } from 'lucide-react'
import SEO from '../components/SEO'

export default function Blog() {
  const [posts, setPosts] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetch('/_api/public/content/blog_posts')
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) {
          const ORDERED_POST_IDS = [
            '54d134aa-f2bb-403f-a375-4d0af52518c1', // 1: Sådan vælger du den rigtige DJ til din fest
            '1e56e007-98f7-44c1-bd26-90056d142fa1', // 2: Vinyl DJ til fest
            '037f0033-f2de-4b70-9d20-fd34ebc3e1b0', // 3: Hvad koster en DJ til Bryllup
            '0d69ca17-f1f1-4757-88ec-fc70939ca29e', // 4: DJ til bryllup – sådan vælger I
            'a68c3162-8b26-4af8-95f4-2b3c0fc9189c', // 5: Dj til fødselsdag – få et kogende dansegulv
            '9c05572c-1a68-4d14-97f5-db8b453f87a2', // 6: DJ til firmafest – sådan vælger du rigtigt
          ]

          const sortedData = [...data].sort((a, b) => {
            const indexA = ORDERED_POST_IDS.indexOf(a.id)
            const indexB = ORDERED_POST_IDS.indexOf(b.id)
            if (indexA !== -1 && indexB !== -1) return indexA - indexB
            if (indexA !== -1) return -1
            if (indexB !== -1) return 1
            return 0
          })

          setPosts(sortedData)
        }
        setLoading(false)
      })
      .catch((err) => {
        console.error('Kunne ikke hente blogindlæg:', err)
        setLoading(false)
      })
  }, [])

  const blogSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Blog",
        "@id": "https://www.djron9.dk/blog/#webpage",
        "url": "https://www.djron9.dk/blog/",
        "name": "Blog, Tips & Inspiration til Din Fest | DJ Ron9",
        "description": "Læs guides og få gode råd om valg af DJ, musik til bryllup, fødselsdag og firmafest. Eksperttips fra DJ Ron9 med over 25 års erfaring.",
        "breadcrumb": {
          "@id": "https://www.djron9.dk/blog/#breadcrumb"
        }
      },
      {
        "@type": "BreadcrumbList",
        "@id": "https://www.djron9.dk/blog/#breadcrumb",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Forside",
            "item": "https://www.djron9.dk/"
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Blog",
            "item": "https://www.djron9.dk/blog/"
          }
        ]
      }
    ]
  }

  return (
    <div className="bg-black text-white min-h-screen pb-20 selection:bg-amber-500 selection:text-black">
      <SEO
        title="Blog, Tips & Inspiration til Din Fest | DJ Ron9"
        description="Læs guides og få gode råd om valg af DJ, musik til bryllup, fødselsdag og firmafest. Eksperttips fra DJ Ron9 med over 25 års erfaring."
        canonical="/blog/"
        schema={blogSchema}
      />
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden">
        <div className="absolute inset-0 z-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[350px] bg-amber-500/10 blur-[130px] rounded-full" />
          <div className="absolute top-1/3 right-10 w-[400px] h-[300px] bg-orange-600/10 blur-[120px] rounded-full" />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-amber-500/30 bg-amber-500/10 text-amber-400 text-xs font-semibold uppercase tracking-wider mb-6">
            <BookOpen className="w-3.5 h-3.5" />
            <span>Inspiration & Guides</span>
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-6xl font-black tracking-tight text-white mb-6">
            Tips & inspiration til <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-200 via-amber-400 to-orange-500">
              din næste fest
            </span>
          </h1>

          <p className="text-lg md:text-xl text-zinc-300 max-w-2xl mx-auto font-light leading-relaxed">
            Få gode råd om musikvalg, timing, udstyr og hvordan du skaber den perfekte stemning til bryllup, fødselsdag og firmafest.
          </p>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="w-10 h-10 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-zinc-400 text-lg">Der er i øjeblikket ingen blogindlæg.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((post) => {
              const title = typeof post.title === 'object' ? post.title?.da : post.title
              const summary = typeof post.summary === 'object' ? post.summary?.da : post.summary
              const rawSlug = typeof post.slug === 'object' ? (post.slug?.da || post.slug) : (post.slug || post.post_slug?.da || post.post_slug || post.id || '')
              const postSlug = (post.post_slug?.da || post.post_slug || rawSlug || '').toLowerCase()
              const author = typeof post.author === 'object' ? post.author?.da : (post.author || 'Ronni Berlau')
              const imageUrl = Array.isArray(post.image) && post.image[0] ? post.image[0] : (typeof post.image === 'string' ? post.image : '')

              let targetUrl = `/blog/${rawSlug}`
              if (postSlug.includes('koster')) targetUrl = '/hvad-koster-en-dj-til-bryllup'
              else if (postSlug.includes('bryllup')) targetUrl = '/dj-til-bryllup-i-koebenhavn-saadan-vaelger-i'
              else if (postSlug.includes('vinyl')) targetUrl = '/vinyl-dj-til-fest'
              else if (postSlug.includes('firmafest')) targetUrl = '/dj-til-firmafest-saadan-vaelger-du-rigtigt'
              else if (postSlug.includes('foedselsdag') || postSlug.includes('fødselsdag')) targetUrl = '/dj-til-foedselsdag-der-fylder-dansegulvet'
              else if (postSlug.includes('vaelg') || postSlug.includes('rigtige') || postSlug.includes('spotify')) targetUrl = '/vaelg-den-rigtige-dj-til-din-fest'

              return (
                <article
                  key={post.id}
                  className="group relative flex flex-col overflow-hidden rounded-2xl border border-amber-500/30 shadow-backlight-hover bg-zinc-950 md:shadow-backlight md:border-zinc-900 md:hover:shadow-backlight-hover md:hover:border-amber-500/30 md:hover:-translate-y-1 transition-all duration-300"
                >
                  {/* Image wrapper */}
                  <Link to={targetUrl} className="relative aspect-video w-full overflow-hidden bg-zinc-900 block">
                    {imageUrl ? (
                      <img
                        src={`https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=80,format=auto/${imageUrl.replace('https://assets.auxodev.dk/', '')}`}
                        alt={title}
                        width="600"
                        height="338"
                        decoding="async"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-zinc-900 text-zinc-700">
                        <Sparkles className="w-12 h-12" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-transparent to-transparent opacity-60" />
                  </Link>

                  {/* Content */}
                  <div className="p-6 md:p-8 flex flex-col flex-grow justify-between">
                    <div>
                      {/* Meta info */}
                      <div className="flex items-center gap-4 text-xs text-zinc-400 mb-3">
                        {post.date && (
                          <div className="flex items-center gap-1.5 text-amber-400">
                            <Calendar className="w-3.5 h-3.5" />
                            <span>{post.date}</span>
                          </div>
                        )}
                        {author && <span>Af {author}</span>}
                      </div>

                      {/* Title */}
                      <h2 className="text-xl font-bold text-white group-hover:text-amber-400 transition-colors duration-200 line-clamp-2 mb-3">
                        <Link to={targetUrl}>{title}</Link>
                      </h2>

                      {/* Summary */}
                      <p className="text-zinc-400 text-sm leading-relaxed line-clamp-3 mb-6">
                        {summary}
                      </p>
                    </div>

                    {/* CTA */}
                    <div className="pt-4 border-t border-zinc-800/80">
                      <Link
                        to={targetUrl}
                        className="inline-flex items-center gap-2 text-sm font-semibold text-amber-400 group-hover:text-amber-300 transition-colors"
                      >
                        <span>Læs hele artiklen</span>
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </Link>
                    </div>
                  </div>
                </article>
              )
            })}
          </div>
        )}
      </section>

      {/* CTA Box */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mt-24">
        <div className="relative rounded-3xl overflow-hidden border border-amber-500/30 bg-gradient-to-b from-zinc-900 to-black p-8 md:p-12 text-center shadow-2xl">
          <div className="absolute top-0 right-0 -translate-y-12 translate-x-12 w-64 h-64 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Klar til at planlægge musikken til din fest?
          </h2>
          <p className="text-zinc-300 max-w-xl mx-auto mb-8 font-light">
            Kontakt mig i dag til en uforpligtende snak om dine ønsker, dato og rammer for arrangementet.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link
              to="/kontakt"
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full bg-gradient-to-r from-amber-400 to-amber-500 text-black font-bold hover:brightness-110 transition-all text-center shadow-lg shadow-amber-500/20"
            >
              <Send className="h-4 w-4" />
              <span>Få et uforpligtende tilbud</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
